import { Navigate, NavLink, useLocation, useNavigate } from 'react-router-dom';
import AccessDenied from 'pages/AccessDenied';
import { useAuth } from 'services/auth-service';
import { navItemsList } from 'utils/navItemsLists';
import { ROLE } from 'utils/roles';

import * as React from 'react';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Logo from 'assets/Logo';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  AppBar,
  Divider
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { colours } from 'utils/constants';

export interface NavItem {
  to: string;
  icon: JSX.Element;
  label: string;
  action?: string;
  subItems?: NavItem[];
}

const drawerWidth = 250;

export default function PrivateRoute({
  children,
  roles
}: {
  children: JSX.Element;
  roles: Array<ROLE | string>;
}) {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { pathname } = useLocation();
  const [subMenuOpen, setSubMenuOpen] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const {
    logout,
    state: { user }
  } = useAuth();

  const [path, setPath] = useState<string>('');

  useEffect(() => {
    if (user.role === 'SUPER' || user.role === 'ACCOUNT') {
      setPath('/accounts');
    } else if (user.role === 'BUILDING') {
      setPath('/buildings');
    } else if (user.role === 'ORG') {
      setPath('/organizations');
    } else {
      setPath('/');
    }
  }, [user.role]);

  if (user.isLoading) {
    return <p className="container">Checking auth..</p>;
  }

  /* The below line is checking if the `user.role` exists and if it is included in the `roles` array. */
  const userHasRequiredRole = user.role && roles.includes(user.role) ? true : false;

  /* If the user is not authenticated, it will redirect them to the home page ("/") with the current location stored in the state object.
  This allows the user to be redirected back to the current page after they have successfully authenticated. */
  if (!user.isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} />;
  }

  /* The code block is checking if the user is authenticated and if not it will redirect to the Access Denied page. */
  if (user.isAuthenticated && !userHasRequiredRole) {
    return <AccessDenied />; // Build your own access denied page (similar to a 404 page)
  }

  /* If `user.role` is undefined or falsy, it will default to an empty string. */
  const userRole = user.role || '';

  /* The below line is assigning the result of the `navItemsList` function to the variable `navItems`, with a type assertion to `NavItem[]`. */
  const navItems = navItemsList(userRole) as NavItem[];

  // Handle Menu Toggle
  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  // Close Drawer on Menu Item select for Persistent Drawer
  const handleDrawerCloseOnClick = () => {
    setMobileOpen(false);
  };

  // Handle Menu Toggle (for mobile Drawer)
  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleLogout = () => {
    navigate('/');
    logout();
  };

  // Display sub menu items
  const handleMenuItemClick = (item: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setSubMenuOpen((prev) => ({ ...prev, [item]: !prev[item] }));
  };

  // Check to determine the selcted menu item from the navbar
  const isActiveMenuItem = (item: NavItem) => {
    if (pathname === item.to) {
      return true;
    }
    if (item.subItems) {
      return item.subItems.some((subItem) => pathname === subItem.to);
    }
    return false;
  };

  // Drawer Content
  const drawerContent = (isPersistentDrawer?: boolean) => (
    <div>
      <Divider />
      <List sx={{ paddingTop: '0px', paddingBottom: '50px' }}>
        {navItems.map((item, index) => (
          <React.Fragment key={index}>
            <ListItem
              component={NavLink}
              to={item.to}
              onClick={(event) =>
                item.subItems
                  ? handleMenuItemClick(item.label, event)
                  : isPersistentDrawer && handleDrawerCloseOnClick()
              }
              className={`items-center !pl-6 ${
                isActiveMenuItem(item) ? 'activeClass' : 'inactiveClass'
              } ${item.subItems && subMenuOpen[item.label] ? '' : 'border-b border-neutral2'}`}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText
                primary={
                  <span
                    className={`${
                      item.to === pathname ? 'bg-primary1 text-primary2 rounded-full px-2 py-1' : ''
                    }`}
                  >
                    {item.label}
                  </span>
                }
              />
              {item.subItems ? subMenuOpen[item.label] ? <ExpandLess /> : <ExpandMore /> : null}
            </ListItem>
            {item.subItems && (
              <Collapse in={subMenuOpen[item.label]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding className="border-b border-neutral2">
                  {item.subItems.map((subItem, subIndex) => (
                    <ListItem
                      key={subIndex}
                      component={NavLink}
                      to={subItem.to}
                      className={`items-center !pl-6 ${
                        subItem.to === pathname ? 'activeClass' : 'inactiveClass'
                      }`}
                      onClick={(event) => {
                        if (subItem.action === 'logout') {
                          event.preventDefault();
                          handleLogout(); // Directly handle logout
                        } else if (isPersistentDrawer) {
                          handleDrawerCloseOnClick();
                        }
                      }}
                    >
                      <ListItemIcon>{subItem.icon}</ListItemIcon>
                      <ListItemText
                        primary={
                          <span
                            className={`${
                              subItem.to === pathname
                                ? 'bg-primary1 text-primary2 rounded-full px-2 py-1'
                                : ''
                            }`}
                          >
                            {subItem.label}
                          </span>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', backgroundColor: colours.lightGrey }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: '#FFFFFF',
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          display: { md: 'none' } // Hide AppBar for medium screen size onwards
        }}
      >
        <Toolbar>
          <div
            style={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            <Logo linkPath={path} />
          </div>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { md: 'none' } // Hide Hamburger menu icon for medium screen size onwards
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 }
        }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="persistent"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', md: 'none' }, // Hide Persistent Drawer for medium screen size onwards
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
        >
          <Toolbar
            sx={{
              justifyContent: 'flex-end',
              width: '100%',
              display: { md: 'none' }
            }}
          >
            <IconButton
              sx={{
                display: 'flex'
              }}
              onClick={handleDrawerClose}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
          {drawerContent(true)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' }, // Hide Permanent Drawer for extra-small and small screen sizes
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, zIndex: 40 }
          }}
          open
        >
          <div
            style={{
              marginTop: '40px',
              marginBottom: '40px',
              marginLeft: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            <Logo linkPath={path} />
          </div>
          {drawerContent(false)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 }, // Adjust padding based on screen size
          width: {
            xs: '100%', // Full width on extra small screens
            sm: '100%', // Full width on small screens
            md: `calc(100% - ${drawerWidth}px)` // Adjust width when drawer is visible
          }
        }}
      >
        <Toolbar
          sx={{
            display: { xs: 'block', md: 'none' } // Display on extra small to small screens, hide on medium and above
          }}
        />
        <Box sx={{ px: 2.5 }}>{children}</Box>
      </Box>
    </Box>
  );
}
